import type { UploadLayout, UploadLayoutDefinition } from '@erp/rpc';

import { AS400_UPLOAD_FIELDS } from './as400UploadFields';
import { FEEDBACK_NEW_MARGIN, feedbackLayoutColumns } from './feedbackLayout';
import { BUILT_IN_LAYOUT_EPOCH } from './supplierLayouts';

export const STELPRO_LAYOUT_NAME = 'Stelpro';

/** Stelpro's RESULTS headers are the AS400 upload field names, in order. */
export const STELPRO_UPLOAD_HEADERS = AS400_UPLOAD_FIELDS;

const NEW_COST_ADDER =
  '[FINAL PRIOR COST ADD] + DEFAULT([Feedback: Core Cost Increase], 0)';
const NEW_MARGIN = FEEDBACK_NEW_MARGIN;

/**
 * The Stelpro layout. Stelpro sends two workbooks: RESULTS, whose columns
 * LOAD NUM … COST ADD FLAG already are the 82 upload columns in order, and
 * Feedback, a per-load table of the margin and core-cost increases to apply.
 * Everything passes through from RESULTS except:
 *
 * - SELL NET: the core cost grossed up by the new cost adder and re-margined at
 *   the new margin, rounded to 2 decimals.
 * - COST ADD FIELD: the new cost adder in points, unrounded like her AC*100;
 *   blank when there is none.
 * - COST ADD FLAG: RESULTS' own value, cleared when COST ADD FIELD is blank.
 *
 * New margin = FINAL PRIOR MARGINS + Margin Increase / 100 (Feedback states it
 * in points); new cost adder = FINAL PRIOR COST ADD + Core Cost Increase (a
 * fraction). A blank increase is none. Rows with no AS400 ITEM, or a CORE COST
 * NET of 0, are left out. Taken from the pricing team's QO - RESULTS workbook.
 */
export const STELPRO_LAYOUT: UploadLayoutDefinition = {
  includeWhenAnyFilled: ['AS400 ITEM'],
  skipWhenZero: ['CORE COST NET'],
  lookup: { label: 'Feedback', matchColumn: 'LOAD NUM', keyColumn: 'Num' },
  columns: feedbackLayoutColumns({
    headers: STELPRO_UPLOAD_HEADERS,
    sellNet: `[CORE COST NET] * (1 + ${NEW_COST_ADDER}) / (1 - (${NEW_MARGIN}))`,
    newCostAdder: NEW_COST_ADDER,
  }),
};

export const BUILT_IN_STELPRO_LAYOUT: UploadLayout = {
  id: '00000000-0000-4000-8000-000000000002',
  name: STELPRO_LAYOUT_NAME,
  definition: STELPRO_LAYOUT,
  createdAt: BUILT_IN_LAYOUT_EPOCH,
  updatedAt: BUILT_IN_LAYOUT_EPOCH,
};
